import { useEffect, useState } from "react";
import { Link, Redirect } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type {
  RecentActivityItem,
  TenantDashboardTodayResponse,
  TenantMeResponse,
  TenantPlansResponse,
  TenantRecentActivityResponse,
} from "@orbit/api-zod";
import { TIER_CATALOG } from "@orbit/api-zod";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";

/**
 * Merchant Dashboard — Phase 13 home page.
 *
 * Two panels:
 *   1. Hero stats grid (today, Asia/Manila local) — completed txns,
 *      revenue, unique customers, points/stamps credited.
 *   2. Recent activity feed — latest 20 events across the tenant
 *      (txn completed/voided/refunded, redemptions issued/redeemed,
 *      adjustments, customer revoked/re-enrolled).
 *
 * Refresh policy (D38, spec Risk 1):
 *   useQuery refetches every 30 s while the tab is FOCUSED. The
 *   `refetchIntervalInBackground: false` flag prevents a parked tab
 *   from hammering the API. The "Updated Xs ago" badge gives the
 *   merchant explicit feedback so they don't wonder if numbers froze.
 *
 * Greeting (Filipino time-of-day):
 *   Magandang umaga / hapon / gabi based on local hour. Recorded as
 *   DECISIONS.md D36 — the cashier-side surfaces stay Tagalog-aware.
 */
const TIER_RANK: Record<"FREE" | "PRO" | "ELITE", number> = {
  FREE: 0,
  PRO: 1,
  ELITE: 2,
};
function tierRank(t: "FREE" | "PRO" | "ELITE"): number {
  return TIER_RANK[t];
}

export function MerchantDashboardPage() {
  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;

  const meQ = useQuery({
    queryKey: ["tenant.me"],
    queryFn: async () => {
      const r = await merchantFetch<TenantMeResponse>("/api/v1/tenant/me");
      if (!r.ok) throw new Error(`${r.code}: ${r.message}`);
      return r.data;
    },
  });

  const todayQ = useQuery({
    queryKey: ["tenant.dashboard.today"],
    queryFn: async () => {
      const r = await merchantFetch<TenantDashboardTodayResponse>(
        "/api/v1/tenant/dashboard/today",
      );
      if (!r.ok) throw new Error(`${r.code}: ${r.message}`);
      return r.data;
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  // Phase 34 (D107) — fetch the tenant's current tier + any pending
  // plan-change request so the greeting can show a "pending" badge
  // until the operator decides. Polls at the same cadence as the
  // dashboard hero so an out-of-band approval is reflected within
  // 30s without a manual refresh.
  const plansQ = useQuery({
    queryKey: ["tenant.plans"],
    queryFn: async () => {
      const r = await merchantFetch<TenantPlansResponse>(
        "/api/v1/tenant/plans",
      );
      if (!r.ok) throw new Error(`${r.code}: ${r.message}`);
      return r.data;
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const activityQ = useQuery({
    queryKey: ["tenant.dashboard.recent-activity"],
    queryFn: async () => {
      const r = await merchantFetch<TenantRecentActivityResponse>(
        "/api/v1/tenant/dashboard/recent-activity?limit=20",
      );
      if (!r.ok) throw new Error(`${r.code}: ${r.message}`);
      return r.data;
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  // Tick once a second so "Updated Xs ago" stays accurate without
  // refetching the API.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const lastUpdated = todayQ.dataUpdatedAt;
  const ago = lastUpdated
    ? Math.max(0, Math.round((now - lastUpdated) / 1000))
    : null;

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-3xl px-6 pt-8">
        <MerchantNav current="dashboard" />

        <header className="mt-6">
          {plansQ.data && plansQ.data.overQuota.length > 0 && (
            <a
              href="/merchant/plans"
              className="mb-3 block rounded-card border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 hover:bg-red-100"
            >
              <strong className="font-semibold">
                You're over your {plansQ.data.currentTier} plan limits.
              </strong>{" "}
              {plansQ.data.overQuota
                .map(
                  (it) =>
                    `${it.label}: ${it.current.toLocaleString()} / ${it.limit.toLocaleString()}`,
                )
                .join(" · ")}{" "}
              — tap to upgrade or reduce usage.
            </a>
          )}
          <h1 className="text-2xl font-bold text-navy">
            {greeting()}
            {meQ.data ? `, ${meQ.data.name}` : ""}
            {(plansQ.data?.currentTier ?? meQ.data?.subscriptionTier) && (
              <a
                href="/merchant/plans"
                title="Manage subscription tier"
                className="ml-2 inline-block rounded-full bg-navy/10 px-2 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-navy hover:bg-navy/20"
              >
                {plansQ.data?.currentTier ?? meQ.data?.subscriptionTier}
              </a>
            )}
            {plansQ.data?.pendingRequest && (
              <a
                href="/merchant/plans"
                title="View your pending plan-change request"
                className="ml-1 inline-block rounded-full bg-amber-200 px-2 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-amber-900 hover:bg-amber-300"
              >
                {(() => {
                  const req = plansQ.data.pendingRequest;
                  const isUpgrade =
                    tierRank(req.requestedTier) > tierRank(req.currentTier);
                  return `${isUpgrade ? "Upgrade" : "Downgrade"} pending: ${
                    TIER_CATALOG[req.requestedTier].name
                  }`;
                })()}
              </a>
            )}
          </h1>
          <p className="mt-1 text-sm text-ink-dark">
            Today's snapshot —{" "}
            {todayQ.data ? formatDateManila(todayQ.data.date) : "loading…"}
            {ago !== null && (
              <span className="ml-2 text-xs text-ink-dark/70">
                · updated {ago}s ago
              </span>
            )}
          </p>
        </header>

        {(todayQ.error || activityQ.error) && (
          <div className="mt-4 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(todayQ.error as Error | undefined)?.message ??
              (activityQ.error as Error | undefined)?.message}
          </div>
        )}

        {/* Hero stats grid */}
        <section className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard
            label="Completed txns"
            value={todayQ.data?.transactions.completed ?? "—"}
          />
          <StatCard
            label="Revenue"
            value={
              todayQ.data ? `₱${formatPeso(todayQ.data.revenuePeso)}` : "—"
            }
          />
          <StatCard
            label="Unique customers"
            value={todayQ.data?.uniqueCustomers ?? "—"}
          />
          <StatCard
            label="Visit-only"
            value={todayQ.data?.visitOnlyTransactions ?? "—"}
            hint="No-spend visits"
          />
        </section>

        {/* Secondary stats */}
        <section className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard
            label="Pts credited"
            value={
              todayQ.data
                ? todayQ.data.loyalty.pointsCredited.toFixed(2)
                : "—"
            }
            tone="muted"
          />
          <StatCard
            label="Stamps credited"
            value={todayQ.data?.loyalty.stampsCredited ?? "—"}
            tone="muted"
          />
          <StatCard
            label="Vouchers issued"
            value={todayQ.data?.redemptions.issued ?? "—"}
            tone="muted"
          />
          <StatCard
            label="Vouchers redeemed"
            value={todayQ.data?.redemptions.redeemed ?? "—"}
            tone="muted"
          />
        </section>

        {/* Voids/refunds/adjustments — small inline summary */}
        {todayQ.data && (
          <p className="mt-3 text-xs text-ink-dark">
            {todayQ.data.transactions.voided} voided ·{" "}
            {todayQ.data.transactions.refunded} refunded ·{" "}
            {todayQ.data.adjustments.count} manual adjustment
            {todayQ.data.adjustments.count === 1 ? "" : "s"}
            {todayQ.data.adjustments.totalDeltaPoints !== 0 &&
              ` (${signed(todayQ.data.adjustments.totalDeltaPoints, 2)} pts)`}
            {todayQ.data.adjustments.totalDeltaStamps !== 0 &&
              ` (${signed(todayQ.data.adjustments.totalDeltaStamps)} stamps)`}
          </p>
        )}

        {/* Recent activity */}
        <section className="mt-8">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-semibold text-navy">Recent activity</h2>
            <Link
              href="/merchant/transactions"
              className="text-xs text-navy underline"
            >
              All transactions →
            </Link>
          </div>
          {activityQ.isLoading && !activityQ.data && (
            <p className="mt-2 text-sm text-ink-dark">Loading…</p>
          )}
          {activityQ.data && activityQ.data.items.length === 0 && (
            <div className="mt-3 rounded-card border border-dashed border-navy/20 px-4 py-6 text-center text-sm text-ink-dark">
              <p>No activity yet today.</p>
              <Link
                href="/merchant/redeem"
                className="mt-2 inline-block text-navy underline"
              >
                Scan a customer to start →
              </Link>
            </div>
          )}
          <ul className="mt-3 space-y-2">
            {activityQ.data?.items.map((item) => (
              <li key={`${item.kind}:${item.sourceId}`}>
                <ActivityRow item={item} />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function greeting(): string {
  // Asia/Manila is UTC+8 with no DST; using the runtime's local time
  // is fine because the device clock is what the cashier sees.
  const h = new Date().getHours();
  if (h < 12) return "Magandang umaga";
  if (h < 18) return "Magandang hapon";
  return "Magandang gabi";
}

function formatDateManila(yyyymmdd: string): string {
  // The server already returned the Asia/Manila wall-clock date as
  // YYYY-MM-DD. Render it locally without any TZ conversion.
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1).toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function formatPeso(s: string): string {
  // Server returns a numeric string with 2 decimals (e.g. "1234.50").
  // Group thousands; keep as-is otherwise.
  const n = Number.parseFloat(s);
  if (Number.isNaN(n)) return s;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function signed(n: number, fractionDigits = 0): string {
  const s = fractionDigits > 0 ? n.toFixed(fractionDigits) : String(n);
  return n > 0 ? `+${s}` : s;
}

function StatCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number | string;
  hint?: string;
  tone?: "muted";
}) {
  return (
    <div
      className={`card ${
        tone === "muted" ? "bg-cream" : "bg-white"
      } px-4 py-3`}
    >
      <p className="text-[10px] uppercase tracking-wider text-ink-dark">
        {label}
      </p>
      <p className="mt-1 font-mono text-xl font-bold text-navy">{value}</p>
      {hint && <p className="mt-0.5 text-[10px] text-ink-dark/70">{hint}</p>}
    </div>
  );
}

function ActivityRow({ item }: { item: RecentActivityItem }) {
  const when = new Date(item.occurredAt);
  const customerHref = item.tenantCustomerId
    ? `/merchant/customers/${item.tenantCustomerId}/${item.customerId}`
    : null;
  // ANONYMIZED customers come back with name="Deleted Customer". The
  // backend already substitutes; we just render literally and skip
  // the deep-link if there's no live tenant_customer relationship.
  const nameNode = customerHref ? (
    <Link href={customerHref} className="font-semibold text-navy hover:underline">
      {item.customerName}
    </Link>
  ) : (
    <span className="font-semibold text-ink-dark">{item.customerName}</span>
  );

  return (
    <div className="card flex items-start justify-between gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-ink-dark">
          <KindBadge kind={item.kind} /> {nameNode}
          {item.amount && ` · ₱${formatPeso(item.amount)}`}
        </p>
        <p className="mt-0.5 text-xs text-ink-dark/70">
          {when.toLocaleString(undefined, {
            hour: "numeric",
            minute: "2-digit",
            month: "short",
            day: "numeric",
          })}
        </p>
      </div>
    </div>
  );
}

function KindBadge({ kind }: { kind: RecentActivityItem["kind"] }) {
  const map: Record<RecentActivityItem["kind"], { label: string; cls: string }> = {
    TRANSACTION_COMPLETED: {
      label: "Sale",
      cls: "bg-green-100 text-green-800",
    },
    TRANSACTION_VOIDED: { label: "Void", cls: "bg-red-100 text-red-800" },
    TRANSACTION_REFUNDED: {
      label: "Refund",
      cls: "bg-amber-100 text-amber-800",
    },
    REDEMPTION_ISSUED: {
      label: "Voucher issued",
      cls: "bg-blue-100 text-blue-800",
    },
    REDEMPTION_REDEEMED: {
      label: "Voucher redeemed",
      cls: "bg-indigo-100 text-indigo-800",
    },
    ADJUSTMENT: { label: "Adjustment", cls: "bg-purple-100 text-purple-800" },
    CUSTOMER_REVOKED: {
      label: "Disconnected",
      cls: "bg-gray-200 text-gray-800",
    },
    CUSTOMER_RE_ENROLLED: {
      label: "Re-enrolled",
      cls: "bg-emerald-100 text-emerald-800",
    },
  };
  const m = map[kind];
  return (
    <span
      className={`mr-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${m.cls}`}
    >
      {m.label}
    </span>
  );
}
